import { Link, useLocation } from 'react-router-dom';
import { AuthUser } from 'aws-amplify/auth';
import { useAdminAccess } from '../../hooks/useAdminAccess';

interface AdminLayoutProps {
  user: AuthUser | undefined;
  signOut: () => void;
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  superAdminOnly?: boolean;
}

const navItems: NavItem[] = [
  { path: '/admin', label: 'Dashboard', icon: '📊' },
  { path: '/admin/tenants', label: 'Tenants', icon: '🏢', superAdminOnly: true },
  { path: '/admin/users', label: 'Users', icon: '👥' },
  { path: '/admin/branding', label: 'Branding', icon: '🎨' },
  { path: '/admin/audit-logs', label: 'Audit Logs', icon: '📋', superAdminOnly: true },
];

export default function AdminLayout({ user, signOut, children }: AdminLayoutProps) {
  const location = useLocation();
  const { isSuperAdmin } = useAdminAccess();

  // Filter nav items based on role
  const visibleNavItems = navItems.filter(
    item => !item.superAdminOnly || isSuperAdmin
  );

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-logo">
            <span className="admin-logo-icon">⚙️</span>
            <span>{isSuperAdmin ? 'Super Admin' : 'Tenant Admin'}</span>
          </div>
        </div>

        <nav className="admin-nav">
          {visibleNavItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              <span className="admin-nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-item">
            <span className="admin-nav-icon">📨</span>
            <span>Back to Inbox</span>
          </Link>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-header">
          <h1>ArchiveAbyss Admin</h1>
          <div className="admin-header-actions">
            <span className="admin-user">{user?.signInDetails?.loginId || 'Admin'}</span>
            <button onClick={signOut} className="admin-btn admin-btn-secondary">
              Sign Out
            </button>
          </div>
        </header>

        <main className="admin-content">
          {children}
        </main>
      </div>
    </div>
  );
}
